<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import SparkleIcon from '@/assets/svg/sparkle.svg?component';
import AIStarAnimationData from '@/assets/json/enrichment-ai-star.json';
import LottieAnimation from '@/components/Common/LottieAnimation.vue';
import { TRACE_PANEL_NAMING, type TracePanelNaming } from '@/components/Common/trace-panel.mapper';

interface Props {
  naming?: TracePanelNaming;
  phase?: 'checking' | 'finalizing';
  reducedMotion?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  naming: TRACE_PANEL_NAMING,
  phase: 'checking',
  reducedMotion: false,
});

const emit = defineEmits<{
  (e: 'message', message: string): void;
}>();

// Fiber and Nexxt lines return once those providers are enabled in production.
const CHECKING_MESSAGES = {
  named: [
    'Querying People Data Labs...',
    'Querying Pipl...',
    'Verifying current phone with Trestle...',
    'Verifying current email with Clearout...',
  ],
  generic: ['Checking multiple sources at once...', 'Cross-referencing what each source found...'],
} as const satisfies Record<TracePanelNaming, readonly string[]>;
const NAMED_MESSAGE_DURATION_MS = 1300;
const MESSAGE_WRAP_HOLD_MS = 1900;
const GENERIC_MESSAGE_DURATION_MS = 1900;
const TICKER_ENTRANCE_DELAY_MS = 700;
const ENTRANCE_DURATION_MS = 2200;
const STAR_FRAMES_PER_SECOND = 60;
const STAR_TOTAL_FRAMES = Number(AIStarAnimationData.op) - Number(AIStarAnimationData.ip);
const WAVE_CYCLE_MS = 3000;
const WAVE_SWEEP_DURATION_MS = 750;

const systemPrefersReducedMotion = ref(false);
const currentMessageIndex = ref(0);
const entryComplete = ref(false);
const starAnimation = ref<InstanceType<typeof LottieAnimation> | null>(null);
const sizer = ref<HTMLElement | null>(null);
const tickerWidth = ref(0);
const wavePosition = ref(200);
const prefersReducedMotion = computed(
  () => props.reducedMotion || systemPrefersReducedMotion.value,
);
const checkingMessages = computed(() => CHECKING_MESSAGES[props.naming]);
const currentMessage = computed(() =>
  props.phase === 'finalizing'
    ? 'Finalizing...'
    : checkingMessages.value[currentMessageIndex.value],
);
const pillStyle = computed(() => ({
  '--enrichment-pill-width': `${tickerWidth.value + 50}px`,
  '--enrichment-wave-position': `${wavePosition.value}%`,
}));

let messageTimeoutId: number | null = null;
let entryTimeoutId: number | null = null;
let animationFrameId: number | null = null;
let clockStartedAt: number | null = null;
let latestElapsedMs = 0;
let reducedMotionQuery: MediaQueryList | null = null;
let sizerObserver: ResizeObserver | null = null;

function stopMessageTicker(): void {
  if (messageTimeoutId !== null) {
    window.clearTimeout(messageTimeoutId);
    messageTimeoutId = null;
  }
}

function currentMessageDuration(): number {
  if (props.naming === 'generic') return GENERIC_MESSAGE_DURATION_MS;
  return currentMessageIndex.value === checkingMessages.value.length - 1
    ? MESSAGE_WRAP_HOLD_MS
    : NAMED_MESSAGE_DURATION_MS;
}

function scheduleNextMessage(delayMs = currentMessageDuration()): void {
  messageTimeoutId = window.setTimeout(() => {
    currentMessageIndex.value = (currentMessageIndex.value + 1) % checkingMessages.value.length;
    scheduleNextMessage();
  }, delayMs);
}

function startMessageTicker(): void {
  stopMessageTicker();
  currentMessageIndex.value = 0;

  if (props.phase !== 'checking' || prefersReducedMotion.value) return;

  scheduleNextMessage(
    Math.max(TICKER_ENTRANCE_DELAY_MS + currentMessageDuration(), ENTRANCE_DURATION_MS),
  );
}

function measureTicker(): void {
  if (!sizer.value) return;
  tickerWidth.value = Math.ceil(
    sizer.value.scrollWidth || sizer.value.getBoundingClientRect().width,
  );
}

function stopSharedClock(): void {
  if (animationFrameId !== null) {
    window.cancelAnimationFrame?.(animationFrameId);
    animationFrameId = null;
  }
  clockStartedAt = null;
  latestElapsedMs = 0;
}

function renderSharedClock(timestamp: number): void {
  if (clockStartedAt === null) clockStartedAt = timestamp;
  latestElapsedMs = timestamp - clockStartedAt;

  const frame = ((latestElapsedMs / 1000) * STAR_FRAMES_PER_SECOND) % STAR_TOTAL_FRAMES;
  starAnimation.value?.goToAndStop(frame, true);

  const waveCycleElapsed = latestElapsedMs % WAVE_CYCLE_MS;
  const sweepProgress = Math.min(1, waveCycleElapsed / WAVE_SWEEP_DURATION_MS);
  wavePosition.value = 200 - sweepProgress * 200;

  animationFrameId = window.requestAnimationFrame(renderSharedClock);
}

function startSharedClock(): void {
  stopSharedClock();
  if (prefersReducedMotion.value || !window.requestAnimationFrame) return;
  animationFrameId = window.requestAnimationFrame(renderSharedClock);
}

function syncStarToClock(): void {
  const frame = ((latestElapsedMs / 1000) * STAR_FRAMES_PER_SECOND) % STAR_TOTAL_FRAMES;
  starAnimation.value?.goToAndStop(frame, true);
}

function handleReducedMotionChange(event: MediaQueryListEvent): void {
  systemPrefersReducedMotion.value = event.matches;
}

watch([() => props.phase, () => props.naming, prefersReducedMotion], startMessageTicker);
watch(currentMessage, async () => {
  await nextTick();
  measureTicker();
});
watch(prefersReducedMotion, (reducedMotion) => {
  if (reducedMotion) {
    entryComplete.value = true;
    stopSharedClock();
  } else {
    startSharedClock();
  }
});
// A button flattens descendants out of the a11y tree, so the host renders the live
// region. Only phase changes are announced; the ticker strings are decorative.
watch(
  () => props.phase,
  (phase) => emit('message', phase === 'finalizing' ? 'Finalizing...' : checkingMessages.value[0]),
  { immediate: true },
);

onMounted(() => {
  reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') ?? null;
  systemPrefersReducedMotion.value = reducedMotionQuery?.matches ?? false;
  reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);
  sizerObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureTicker);
  if (sizer.value) sizerObserver?.observe(sizer.value);
  measureTicker();
  startMessageTicker();
  startSharedClock();

  if (prefersReducedMotion.value) {
    entryComplete.value = true;
  } else {
    entryTimeoutId = window.setTimeout(() => {
      entryComplete.value = true;
      entryTimeoutId = null;
    }, ENTRANCE_DURATION_MS);
  }
});

onBeforeUnmount(() => {
  stopMessageTicker();
  stopSharedClock();
  if (entryTimeoutId !== null) window.clearTimeout(entryTimeoutId);
  sizerObserver?.disconnect();
  reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
});
</script>

<template>
  <button
    type="button"
    data-test-id="enrichment-checking-pill"
    aria-label="Freshness check in progress"
    class="enrichment-checking-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight-300"
    :class="{
      'enrichment-checking-pill--entered': entryComplete,
      'enrichment-checking-pill--reduced-motion': prefersReducedMotion,
    }"
    :style="pillStyle"
    @click.stop
  >
    <span class="enrichment-checking-pill__star" aria-hidden="true">
      <SparkleIcon
        v-if="prefersReducedMotion"
        class="h-4 w-4 text-highlight-500"
        viewBox="0 0 16 20"
      />
      <LottieAnimation
        v-else
        ref="starAnimation"
        :animation-data="AIStarAnimationData"
        :loop="false"
        :autoplay="false"
        width="22px"
        height="22px"
        @animation:loaded="syncStarToClock"
      />
    </span>

    <span
      class="enrichment-checking-pill__ticker"
      :style="{ width: `${tickerWidth}px` }"
      aria-hidden="true"
    >
      <span ref="sizer" class="enrichment-checking-pill__sizer">{{ currentMessage }}</span>
      <Transition name="enrichment-ticker">
        <span
          :key="currentMessage"
          data-test-id="enrichment-checking-message"
          class="enrichment-checking-pill__message"
        >
          {{ currentMessage }}
        </span>
      </Transition>
    </span>
  </button>
</template>

<style scoped lang="postcss">
.enrichment-checking-pill {
  @apply inline-flex h-7 min-w-0 max-w-full items-center overflow-hidden rounded-full;
  @apply bg-highlight-500/[0.07] py-0 pl-[7px] pr-[13px] text-xs font-650 leading-[18px];
  @apply text-highlight-500;
  animation:
    enrichment-pill-visible 350ms 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both,
    enrichment-pill-expand 1100ms 1050ms cubic-bezier(0.16, 1.18, 0.32, 1) both;
}

.enrichment-checking-pill--entered {
  width: auto;
  animation: none;
}

.enrichment-checking-pill__star {
  @apply flex h-[22px] w-[22px] shrink-0 items-center justify-center;
  animation: enrichment-pill-star-pop 350ms 700ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.enrichment-checking-pill__ticker {
  @apply relative ml-2 inline-flex min-w-0 items-center overflow-hidden transition-[width] duration-300;
  animation: enrichment-pill-text-in 1100ms 1050ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.enrichment-checking-pill__sizer {
  @apply invisible whitespace-nowrap;
}

.enrichment-checking-pill__message {
  @apply absolute left-0 top-1/2 whitespace-nowrap;
  background-image: linear-gradient(
    100deg,
    theme('colors.highlight.500') 0%,
    theme('colors.highlight.500') 38%,
    theme('colors.enrichment.wave') 50%,
    theme('colors.highlight.500') 62%,
    theme('colors.highlight.500') 100%
  );
  background-position: var(--enrichment-wave-position) 0;
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  transform: translateY(-50%);
}

.enrichment-ticker-enter-active,
.enrichment-ticker-leave-active {
  transition:
    opacity 550ms cubic-bezier(0.86, 0, 0.07, 1),
    transform 550ms cubic-bezier(0.86, 0, 0.07, 1),
    filter 550ms cubic-bezier(0.86, 0, 0.07, 1);
}

.enrichment-ticker-enter-from {
  opacity: 0;
  filter: blur(2.2px);
  transform: translateY(calc(-50% + 10px));
}

.enrichment-ticker-leave-to {
  opacity: 0;
  filter: blur(2.2px);
  transform: translateY(calc(-50% - 9px));
}

@keyframes enrichment-pill-visible {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes enrichment-pill-star-pop {
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes enrichment-pill-expand {
  0% {
    width: 28px;
  }
  72% {
    width: calc(var(--enrichment-pill-width) + 12px);
  }
  100% {
    width: var(--enrichment-pill-width);
  }
}

@keyframes enrichment-pill-text-in {
  from {
    opacity: 0.3;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .enrichment-checking-pill,
  .enrichment-checking-pill__star,
  .enrichment-checking-pill__ticker {
    animation: none;
  }

  .enrichment-ticker-enter-active,
  .enrichment-ticker-leave-active,
  .enrichment-checking-pill__ticker {
    transition: none;
  }
}

.enrichment-checking-pill--reduced-motion {
  width: auto;
  animation: none;
}

.enrichment-checking-pill--reduced-motion .enrichment-checking-pill__star,
.enrichment-checking-pill--reduced-motion .enrichment-checking-pill__ticker {
  animation: none;
}
</style>
